import React, { useState, useEffect, useCallback } from 'react';
import AppIcon from '../components/AppIcon';

export interface Toast {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  durationMs?: number;
}

export function useToast() {
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    if (!toast) return;
    const ms = toast.durationMs ?? (toast.type === 'success' ? 6000 : 7000);
    const timer = setTimeout(() => setToast(null), ms);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((message: string, type: Toast['type'] = 'success', durationMs?: number) => {
    setToast({ message, type, durationMs });
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  return { toast, showToast, dismissToast };
}

const toastColors: Record<Toast['type'], string> = {
  success: 'bg-emerald-600',
  error: 'bg-destructive',
  info: 'bg-primary',
  warning: 'bg-amber-600',
};

export function ToastRenderer({ toast, onDismiss }: { toast: Toast | null; onDismiss: () => void }) {
  if (!toast) return null;
  // Assertive announcements interrupt the screen reader for problems the user must
  // notice (error/warning); polite ones wait their turn (success/info).
  const assertive = toast.type === 'error' || toast.type === 'warning';
  return React.createElement(
    'div',
    {
      role: assertive ? 'alert' : 'status',
      'aria-live': assertive ? 'assertive' : 'polite',
      'data-testid': 'app-toast',
      // The toast's severity, exposed so a test can tell a refusal from a success.
      // Without it every toast looks alike to a selector, and a lingering success
      // toast ("Your subscription has been cancelled.") reads as a checkout refusal.
      'data-toast-type': toast.type,
      className: `fixed bottom-4 end-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white ${toastColors[toast.type]}`,
    },
    React.createElement('span', null, toast.message),
    React.createElement(
      'button',
      { onClick: onDismiss, className: 'ms-2 hover:opacity-80', 'aria-label': 'Dismiss' },
      React.createElement(AppIcon, { name: 'X', size: 16 })
    )
  );
}
